import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, CreditCard, AlertTriangle } from "lucide-react";
import { api, errorText } from "@/lib/api";
import { Btn, Input } from "@/forum/ui";
import { Shell } from "@/pages/PasswordPages";

const huf = (n) => `${Number(n).toLocaleString("hu-HU")} Ft`;

export function DonatePage() {
  const [packages, setPackages] = useState([]);
  const [selected, setSelected] = useState("");
  const [accountId, setAccountId] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/donate/packages")
      .then(({ data }) => {
        setPackages(data);
        if (data.length) setSelected(data[0].id);
      })
      .catch((ex) => setErr(errorText(ex)));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/donate/checkout", { account_id: Number(accountId), package_id: selected });
      window.location.href = data.url;
    } catch (ex) {
      setErr(errorText(ex));
      setBusy(false);
    }
  };

  return (
    <Shell testId="donate-page">
      <p className="eyebrow mb-3">Szervertámogatás</p>
      <h1 className="font-display text-3xl font-bold">PP shop</h1>
      <form onSubmit={submit} className="mt-8 space-y-4">
        <p className="text-sm text-white/55">
          Válassz csomagot, add meg a játékbeli account ID-dat, és a sikeres fizetés után a PP (prémiumpont) automatikusan jóváírásra kerül.
        </p>
        <div className="grid grid-cols-2 gap-3" role="radiogroup" aria-label="Csomagok">
          {packages.map((p) => (
            <button
              key={p.id}
              type="button"
              role="radio"
              aria-checked={selected === p.id}
              onClick={() => setSelected(p.id)}
              data-testid={`donate-package-${p.id}`}
              className={`rounded-2xl border px-4 py-4 text-left transition-colors ${
                selected === p.id ? "border-[rgba(94,177,255,0.7)] bg-[rgba(94,177,255,0.08)]" : "border-white/10 hover:border-white/25"
              }`}
            >
              <span className="block font-display text-lg font-bold text-white">{p.sp.toLocaleString("hu-HU")} PP</span>
              <span className="block text-sm text-white/55">{huf(p.price)}</span>
            </button>
          ))}
        </div>
        <Input
          type="number"
          min={1}
          value={accountId}
          onChange={(e) => setAccountId(e.target.value)}
          placeholder="MTA account ID"
          required
          data-testid="donate-account-id"
        />
        <p className="text-xs text-white/40">Figyelj, hogy pontosan add meg az account ID-t – rossz ID esetén más kapja meg a PP-t.</p>
        {err && <p className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-300" data-testid="donate-error">{err}</p>}
        <Btn type="submit" disabled={busy || !selected} className="btn-glow w-full" data-testid="donate-submit">
          <CreditCard size={16} className="mr-2 inline" />
          {busy ? "Átirányítás…" : "Fizetés kártyával"}
        </Btn>
        <p className="text-center text-xs text-white/40">A fizetést a Stripe biztonságos oldala kezeli.</p>
      </form>
    </Shell>
  );
}

export function DonateSuccessPage() {
  const [sp] = useSearchParams();
  const sessionId = sp.get("session_id") || "";
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!sessionId) return undefined;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/donate/session/${encodeURIComponent(sessionId)}`);
        setInfo(data);
        if (data.status !== "credited" && data.status !== "mismatch" && ++tries < 20) timer = setTimeout(poll, 3000);
      } catch (ex) { setErr(errorText(ex)); }
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  const credited = info?.status === "credited";
  const mismatch = info?.status === "mismatch";

  return (
    <Shell testId="donate-success-page">
      <p className="eyebrow mb-3">Szervertámogatás</p>
      <h1 className="font-display text-3xl font-bold">Köszönjük a támogatást!</h1>
      <div className="mt-8 space-y-4">
        <span className="grid h-12 w-12 place-items-center rounded-full border border-[rgba(94,177,255,0.5)] text-[var(--glow)]">
          {mismatch ? <AlertTriangle size={20} /> : credited ? <CheckCircle2 size={20} /> : <Clock size={20} />}
        </span>
        {err ? (
          <p className="text-sm text-red-300" data-testid="donate-success-error">{err}</p>
        ) : mismatch ? (
          <p className="text-sm text-white/70">A fizetés adatai eltérnek, ezért nem írtuk jóvá automatikusan. Keress meg egy adminisztrátort.</p>
        ) : credited ? (
          <p className="text-sm text-white/70" data-testid="donate-credited">
            A(z) <strong className="text-white">{info.package}</strong> csomagot jóváírtuk a(z) <strong className="text-white">#{info.account_id}</strong> fiókon.
          </p>
        ) : (
          <p className="text-sm text-white/70" data-testid="donate-pending">
            A fizetés feldolgozása folyamatban van, a PP néhány percen belül megérkezik a fiókodra.
          </p>
        )}
        <Link to="/" className="inline-block text-sm text-white underline-offset-4 hover:underline">Vissza a főoldalra</Link>
      </div>
    </Shell>
  );
}
